import React, { useState, useEffect, useMemo, useRef } from "react";
import { Breadcrumb } from "../../components/Breadcrumb";
import { FORM_CONSTANTS } from "./constants";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { useLocation, useParams } from "react-router-dom";
import { FormData } from "./types";
import { ActionButtons } from "../../utils/renderActionButtons";
import {
  useBonusDetails,
  useJsonTemplates,
  useProjects,
} from "../../hooks/useBonusQueries";
import { BonusApplicationComment, BonusApplicationHistoryItem, Project } from "../../services/bonus/types";
import { getPreviousQuarter } from "../../utils/dateUtils";
import CommentComponent from "../../components/SubForms/Common/CommentComponent";
import BonusApplicationHistory from "../../components/SubForms/Common/BonusApplicationHistory";
import { useComments, useHistory } from "../../hooks/useFormHelpers";
import SubdivisionProductionSubForm from "../../components/SubForms/SubdivisionProductionSubForm";
import ExtraLaborAndMaterialSubForm from "../../components/SubForms/ExtraLaborAndMaterialSubForm";
import CustomerCareExpenseSubForm from "../../components/SubForms/CustomerCareExpenseSubForm";
import CustomerCareCallsSubForm from "../../components/SubForms/CustomerCareCallsSubForm";
import PostCloseTouchpointSubForm from "../../components/SubForms/PostCloseTouchpointSubForm";
import SurveysSubForm from "../../components/SubForms/SurveysSubForm";
import BonusCalculationCM3PMSubForm from "../../components/SubForms/BonusCalculationsCM3PMSubForm";
import { getBonusSheetJsonData, parseBonusSheetJsonData } from "../../utils/bonusSheetParser";
import Select, { components, GroupProps, GroupHeadingProps } from 'react-select';

// Helper function for immutable nested state updates
const setByPath = <T,>(obj: T, path: string[], value: any): T => {
  const [head, ...rest] = path;
  if (!head) return obj;
  return {
    ...(obj as any),
    [head]: rest.length
      ? setByPath((obj as any)[head] ?? {}, rest, value)
      : value,
  } as T;
};

const CM3PMForm: React.FC = () => {
const location = useLocation();
  const selectedRole = localStorage.getItem("selectedRole") || "";
  const { id } = useParams<{ id: string }>();
  const numericId = parseInt(id || "0");
  const hasValidId = numericId > 0;
  const { data: templates, isLoading: isLoadingTemplates, error: templatesError } = useJsonTemplates(selectedRole);
  const { data: bonusDetails, isLoading: isLoadingBonusDetails, error: bonusDetailsError } = useBonusDetails(numericId);
  const { data: projectsData, isLoading: isLoadingProjects, error: projectsError } = useProjects();

  const under7DebounceRef = useRef<NodeJS.Timeout | null>(null);
  const meetingsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [bonusSheetData, setBonusSheetData] = useState<any>(null);

  const queryParams = new URLSearchParams(location.search);
  const isReviewMode = queryParams.get("mode") === "review";
  const exEmployeeQueryParam = queryParams.get("data") || null;
  const vpQueryParam = queryParams.get("vp") || null;
  const prevQuarterYear = getPreviousQuarter();
  const normalizedStatus = (bonusDetails?.currentStatus || "")
    .toString()
    .trim()
    .replace(/ /g, "_")
    .toLowerCase();
  const isEditable = !bonusDetails || ["draft", "rejected"].includes(normalizedStatus);
  const [loggedInUser, setLoggedInUser] = useState({ name: localStorage.getItem("loggedInUserFullName") ?? "", email: localStorage.getItem("loggedInUserEmail") ?? "" });

  const isLoading = isLoadingTemplates || isLoadingBonusDetails || isLoadingProjects;
  const projects = Array.isArray(projectsData) ? projectsData : [];
  const initQueryParam = queryParams.get("init") || null;

  const [bonusSheetTitle, setBonusSheetTitle] = useState<string>(`${selectedRole} Quarterly Bonus`);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [applicantInfo, setApplicantInfo] = useState({
    name: localStorage.getItem("loggedInUserFullName") ?? "",
    email: localStorage.getItem("loggedInUserEmail") ?? "",
  });

  const { comment, commentList, commentsAccordionExpanded, handleCommentChange, handleAddComment, handleCommentsAccordionToggle } = useComments(bonusDetails, selectedRole);
  const { historyItems, historyAccordionExpanded, handleHistoryAccordionToggle } = useHistory(bonusDetails);

  const [formData, setFormData] = useState<FormData>({
    managerName: "",
    managerEmail: "",

    applicationType: "",
    bonusRecipientEmail: "",
    bonusRecipientName: "",
    submitterName: (localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "",
    submitterEmail: (localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "",
    vpName: "",
    vpEmail: "",
    opsName: FORM_CONSTANTS.OPS.NAME,
    opsEmail: FORM_CONSTANTS.OPS.EMAIL,
    communityNumber: "",
    year: prevQuarterYear.year.toString(),
    quarter: prevQuarterYear.quarter.toString(),

    hasOverriddenHierarchy: false,
    jobTitle: "",
    jobFamily: "",

    operationalEfficiency: {
      subdivisionProduction: { benchmark: "", actualProduction: "", benchmarkPercent: "", percentOfBenchmark: "", bonusPercentEarned: "", bonusValue: "0.35", score: "" },
      extraLaborAndMaterial: { benchmark: "", actualELM: "", goal: "", percentOfRevenue: "", bonusPercentEarned: "", bonusValue: "0.15", score: "" },
      customerCareExpense: { actualExpense: "", budget: "", percentOfBudget: "", bonusPercentEarned: "", bonusValue: "0.1", score: "" },
    },
    customerExperience: {
      customerCareCalls: { benchmark: "70", under7Completed: "", totalCallsCompleted: "", percentCompletedUnder7: "0.0", percentVsBenchmark: "", bonusPercentEarned: "", bonusValue: "0.2", score: "" },
      postCloseTouchpoint: { benchmark: "100%", meetingsCompleted30Days: "", totalMeetings: "", percentCompleted: "", percentVsBenchmark: "", bonusPercentEarned: "", bonusValue: "0.1", score: "" },
      surveys: {
        trustBuilderScore: { surveysReceived: "", sumOfScores: "", avgScore: "" },
        closingSurveyScore: { complete: "", onTime: "", customerRatingScore: "" },
        overallCombinedSurveyScore: { trustBuilders: "", complete: "", onTime: "", customerRatingScore: "", totalAvgScore: "" },
        bonusPercentEarned: "", bonusValue: "0.1", score: "",
      },
    },
    bonusCalculation: {
      totalAchievementScore: { revenueOfHomesClosed: "", overridePercentage: "", bonusPoolAvailableAt100: "", totalMaxAt120: "" },
      totalScore: "", percentage: "", bonusPool: "", totalBonusPayout: "",
    },
    signatures: { divisionManagerSignature: "", divisionManagerDate: "", vpSignature: "", vpDate: "", employeeSignature: "", employeeDate: "" },
  });

  //#region Custom Community Select Logic

  // Group the projects by hierLevel3 (Region)
  const groupedOptions = Object.entries(
    projects.reduce((acc, project) => {
      const region = project.hierLevel3 || "Other";
      if (!acc[region]) acc[region] = [];
      acc[region].push({
        value: project.projectName, // Matches your original value logic
        label: `${project.projectNumber} - ${project.projectName}`,
        region: project.hierLevel3,
        projectNumber: project.projectNumber,
        projectName: project.projectName
      });
      return acc;
    }, {} as Record<string, any[]>)
  )
    .sort((a, b) => a[0].localeCompare(b[0])) // sort regions alphabetically
    .map(([region, options]) => ({
      label: region,
      options: options.sort((a, b) => a.projectName.localeCompare(b.projectName))
    }));

  // Define custom filter logic for typeahead
  const filterOption = (candidate: any, input: string) => {
    const search = input.toLowerCase();
    const data = candidate.data;
    return (
      data.projectName.toLowerCase().includes(search) ||
      data.projectNumber.toLowerCase().includes(search) ||
      data.region.toLowerCase().includes(search)
    );
  };

  // Get all unique region names to start them as collapsed
  const allRegionNames = useMemo(() => groupedOptions.map(g => g.label), [groupedOptions]);

  // Manage Community collapse state
  const [collapsedCommunityGroups, setCollapsedCommunityGroups] = useState<string[]>(allRegionNames);

  const toggleCommunityGroup = (groupLabel: string) => {
    setCollapsedCommunityGroups(prev =>
      prev.includes(groupLabel)
        ? prev.filter(label => label !== groupLabel)
        : [...prev, groupLabel]
    );
  };

  // Custom CommunityGroupHeading with toggle indicator
  const CustomCommunityGroupHeading = (props: GroupHeadingProps<any>) => {
    const isCollapsed = collapsedCommunityGroups.includes(props.children as string);
    return (
      <div
        onClick={() => toggleCommunityGroup(props.children as string)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <components.GroupHeading {...props} />
        <span style={{ fontSize: 10, marginRight: 10 }}>
          {isCollapsed ? '▶' : '▼'}
        </span>
      </div>
    );
  };

  // Custom Group that conditionally hides children based on collapse state
  const CustomCommunityGroup = (props: GroupProps<any>) => {
    const isCollapsed = collapsedCommunityGroups.includes(props.label as string);
    return (
      <components.Group {...props}>
        {!isCollapsed && props.children}
      </components.Group>
    );
  };

  //#endregion Custom Community Select Logic


  const initQueryData = useMemo(() => {
    if (!initQueryParam) return null;
    try {
      return JSON.parse(atob(decodeURIComponent(initQueryParam)));
    } catch (e) {
      console.error("Failed to parse ?init= param", e);
      return null;
    }
  }, [initQueryParam]);


  // ── All useEffects and logic preserved exactly ────────────────────────────

  useEffect(() => {
    if (!initQueryData) return;
    if (bonusDetails || hasValidId) return;
    try {
      setBonusSheetTitle(`${initQueryData.applicationType} Quarterly Bonus`);
      setFormData((prev) => ({
        ...prev,
        managerName: initQueryData.bonusRecipientName || prev.managerName,
        managerEmail: initQueryData.bonusRecipientEmail || prev.managerEmail,

        applicationType: initQueryData.applicationType || prev.applicationType,
        bonusRecipientName: initQueryData.bonusRecipientName || prev.bonusRecipientName,
        bonusRecipientEmail: initQueryData.bonusRecipientEmail || prev.bonusRecipientEmail,
        submitterName: initQueryData.submitterName || prev.submitterName,
        submitterEmail: initQueryData.submitterEmail || prev.submitterEmail,
        vpName: initQueryData.vpName || prev.vpName,
        vpEmail: initQueryData.vpEmail || prev.vpEmail,
        year: initQueryData.year?.toString() || prev.year,
        quarter: initQueryData.quarter?.toString() || prev.quarter,

        hasOverriddenHierarchy: initQueryData?.hasOverriddenHierarchy || false,
        jobTitle: initQueryData?.jobTitle || prev.jobTitle,
        jobFamily: initQueryData?.jobFamily || prev.jobFamily,


        signatures: {
          ...prev.signatures,
          divisionManagerSignature: initQueryData.submitterName || prev.signatures.divisionManagerSignature,
          vpSignature: initQueryData.vpName || prev.signatures.vpSignature
        },
      }));
      setApplicantInfo({ name: initQueryData.bonusRecipientName || "", email: initQueryData.bonusRecipientEmail || "" });
    } catch (e) { console.error("Failed to parse ?init= param", e); }
  }, [initQueryData]);

  useEffect(() => {
    if (!vpQueryParam) return;
    try {
      const vpData = JSON.parse(atob(decodeURIComponent(vpQueryParam)));
      if (vpData?.vpName) {
        setFormData((prev) => {
          if (prev.signatures?.vpSignature) return prev;
          return {
            ...prev,
            signatures: {
              ...prev.signatures,
              divisionManagerSignature: "",
              vpSignature: vpData.vpName
            }
          };
        });
      }
    } catch { }
  }, [vpQueryParam]);

  useEffect(() => {
    if (!bonusDetails && !hasValidId && !initQueryData && templates && templates?.jsonData && templates?.hierarchy) {
      try {
        const templateData = JSON.parse(templates.jsonData);
        if (templates.hierarchy?.immediateSupervisorName && templates.hierarchy?.immediateSupervisorEmail) {
          setApplicantInfo({ name: templates.hierarchy?.immediateSupervisorName, email: templates.hierarchy?.immediateSupervisorEmail });
        }
        setFormData((prev: FormData) => ({
          ...prev,
          // managerName: ((templates.hierarchy?.employeeName || localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "").toString(),
          // managerEmail: ((templates.hierarchy?.employeeEmail || localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "").toString(),
          managerName: "",
          managerEmail: "",

          applicationType: templateData.applicationType || prev.applicationType,
          bonusRecipientName: "",
          bonusRecipientEmail: "",
          submitterName: (localStorage.getItem("loggedInUserFullName") || "").replace(/"/g, "") || "",
          submitterEmail: (localStorage.getItem("loggedInUserEmail") || "").replace(/"/g, "") || "",
          vpName: "",
          vpEmail: "",

          jobTitle: prev.jobTitle || templateData.jobTitle || "",
          jobFamily: prev.jobFamily || templateData.jobFamily || "",
          hasOverriddenHierarchy: templateData?.hasOverriddenHierarchy || false,

          operationalEfficiency: {
            subdivisionProduction: templateData?.operationalEfficiency?.subdivisionProduction || prev.operationalEfficiency.subdivisionProduction,
            extraLaborAndMaterial: templateData?.operationalEfficiency?.extraLaborAndMaterial || prev.operationalEfficiency.extraLaborAndMaterial,
            customerCareExpense: templateData?.operationalEfficiency?.customerCareExpense || prev.operationalEfficiency.customerCareExpense,
          },
          customerExperience: {
            customerCareCalls: templateData?.customerExperience?.customerCareCalls || prev.customerExperience.customerCareCalls,
            postCloseTouchpoint: templateData?.customerExperience?.postCloseTouchpoint || prev.customerExperience.postCloseTouchpoint,
            surveys: templateData?.customerExperience?.surveys || prev.customerExperience.surveys,
          },
          bonusCalculation: {
            // For new forms, do not prefill revenue from template defaults/hardcoded values.
            totalAchievementScore: {
              ...(templateData?.bonusCalculation?.totalAchievementScore || prev.bonusCalculation.totalAchievementScore),
              revenueOfHomesClosed: "",
            },
            totalScore: templateData?.bonusCalculation?.totalScore || prev.bonusCalculation.totalScore,
            percentage: templateData?.bonusCalculation?.percentage || prev.bonusCalculation.percentage,
            bonusPool: templateData?.bonusCalculation?.bonusPool || prev.bonusCalculation.bonusPool,
            totalBonusPayout: templateData?.bonusCalculation?.totalBonusPayout || prev.bonusCalculation.totalBonusPayout,
          },
          signatures: templateData?.signatures || prev.signatures,
        }));
      } catch (error) { console.error("Error parsing template data:", error); }
    }
  }, [templates]);

  useEffect(() => {
    if (bonusDetails && hasValidId) {
      try {
        const rawBonusSheet = getBonusSheetJsonData(bonusDetails);
        if (!rawBonusSheet) return;
        const bsd = parseBonusSheetJsonData(rawBonusSheet) as any;
        setBonusSheetData(bsd);

        setBonusSheetTitle(`${bonusDetails.applicationType} Quarterly Bonus`);

        setFormData((prev) => {
          const recipientNameLoaded = bsd?.bonusRecipientName || bsd?.managerName || bsd?.applicantName || bonusDetails?.employeeName || prev.bonusRecipientName;
          const recipientEmailLoaded = bsd?.bonusRecipientEmail || bsd?.managerEmail || bsd?.applicantEmail || bonusDetails?.bonusRecipientEmail || prev.bonusRecipientEmail;
          return {
            ...prev,
            // TODO: Update this in Edit/Review step
            managerName: recipientNameLoaded,
            managerEmail: recipientEmailLoaded,

            applicationType: bonusDetails?.applicationType.toString() || prev.applicationType,
            bonusRecipientName: recipientNameLoaded,
            bonusRecipientEmail: recipientEmailLoaded,
            submitterName: bonusDetails?.submittedByName || prev.submitterName,
            submitterEmail: bonusDetails?.submittedBy || prev.submitterEmail,
            vpName: bsd?.vpName || prev.vpName,
            vpEmail: bsd?.vpEmail || prev.vpEmail,
            opsName: bsd?.opsName || prev.opsName,
            opsEmail: bsd?.opsEmail || prev.opsEmail,

            jobTitle: bsd?.jobTitle || prev.jobTitle,
            jobFamily: bsd?.jobFamily || prev.jobFamily,
            hasOverriddenHierarchy: bonusDetails?.hasOverriddenHierarchy || false,

            communityNumber: bsd?.communityNumber || prev.communityNumber,
            year: bsd?.year || prev.year,
            quarter: bsd?.quarter || prev.quarter,
            operationalEfficiency: bsd?.operationalEfficiency || prev.operationalEfficiency,
            customerExperience: bsd?.customerExperience || prev.customerExperience,
            bonusCalculation: bsd?.bonusCalculation || prev.bonusCalculation,
            signatures: bsd?.signatures || prev.signatures,
          };
        });

        if (bsd?.communityNumber && projects.length > 0) {
          const project = projects.find(p => p.projectNumber === bsd.communityNumber);
          if (project) setSelectedProject(project);
        }

        if (bonusDetails.createdBy) setApplicantInfo((prev) => ({ ...prev, email: bonusDetails.createdBy }));
      } catch (error) { console.error("Error parsing bonus sheet data:", error); }
    }
  }, [bonusDetails, id]);

  const derivedCalculations = useMemo(() => {
    const subdivisionBenchmark = parseFloat(formData.operationalEfficiency.subdivisionProduction.benchmark || "0");
    const subdivisionActualProduction = parseFloat(formData.operationalEfficiency.subdivisionProduction.actualProduction || "0");
    const subdivisionPercentOfBenchmark = subdivisionBenchmark ? (subdivisionActualProduction / subdivisionBenchmark) : 0;
    const subdivisionBonusPercentEarned = Math.max(0, Math.min(subdivisionPercentOfBenchmark, 1.2)) * (subdivisionPercentOfBenchmark >= 0.7 ? 100 : 0);
    const subdivisionBonusValue = parseFloat(formData.operationalEfficiency.subdivisionProduction.bonusValue || "0.35");
    const subdivisionScore = subdivisionBonusPercentEarned * subdivisionBonusValue;
    const elmBenchmark = parseFloat(formData.operationalEfficiency.extraLaborAndMaterial.benchmark || "0");
    const elmActual = parseFloat(formData.operationalEfficiency.extraLaborAndMaterial.actualELM || "0");
    const elmGoal = elmBenchmark;
    const elmPercentOfRevenue = elmActual && elmGoal ? (elmActual / elmGoal) : 0;
    const elmInversePercent = elmPercentOfRevenue !== 0 ? 1 / elmPercentOfRevenue : 0;
    const elmBonusPercentEarned = Math.max(0, Math.min(elmInversePercent, 1.2)) * (elmInversePercent >= 0.7 ? 100 : 0);
    const elmBonusValue = parseFloat(formData.operationalEfficiency.extraLaborAndMaterial.bonusValue || "0.15");
    const elmScore = elmBonusPercentEarned * elmBonusValue;
    const expenseActual = parseFloat(formData.operationalEfficiency.customerCareExpense.actualExpense || "0");
    const expenseBudget = parseFloat(formData.operationalEfficiency.customerCareExpense.budget || "0");
    const expensePercentOfBudget = expenseBudget ? (expenseActual / expenseBudget) * 1 : 0;
    const expenseInversePercent = expensePercentOfBudget !== 0 ? 1 / expensePercentOfBudget : 0;
    const expenseBonusPercentEarned = Math.max(0, Math.min(expenseInversePercent, 1.20)) * (expenseInversePercent >= 0.7 ? 1 : 0) * 100;
    const expenseBonusValue = parseFloat(formData.operationalEfficiency.customerCareExpense.bonusValue || "0.1");
    const expenseScore = expenseBonusPercentEarned * expenseBonusValue;
    const callsUnder7 = parseFloat(formData.customerExperience.customerCareCalls.under7Completed || "0");
    const callsTotal = parseFloat(formData.customerExperience.customerCareCalls.totalCallsCompleted || "0");
    const callsPercentUnder7 = callsTotal ? (callsUnder7 / callsTotal) * 100 : 0;
    const callsBenchmark = 70;
    const callsPercentVsBenchmark = (callsPercentUnder7 / callsBenchmark) * 100;
    const callsBonusPercentEarned = Math.max(0, Math.min(callsPercentVsBenchmark, 120)) * (callsPercentVsBenchmark >= 70 ? 1 : 0);
    const callsBonusValue = parseFloat(formData.customerExperience.customerCareCalls.bonusValue || "0.2");
    const callsScore = callsBonusPercentEarned * callsBonusValue;
    const meetingsCompleted = parseFloat(formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days || "0");
    const meetingsTotal = parseFloat(formData.customerExperience.postCloseTouchpoint.totalMeetings || "0");
    const meetingsPercentCompleted = meetingsTotal ? (meetingsCompleted / meetingsTotal) * 100 : 0;
    const meetingsBenchmark = 100;
    const meetingsPercentVsBenchmark = (meetingsPercentCompleted / meetingsBenchmark) * 100;
    // if Bonus% is below 70, score should be 0.
    const meetingsBonusPercentEarned = meetingsPercentVsBenchmark < 70 ? 0 : Math.min(meetingsPercentVsBenchmark, 100);
    const meetingsBonusValue = parseFloat(formData.customerExperience.postCloseTouchpoint.bonusValue || "0.1");
    const meetingsScore = meetingsBonusPercentEarned * meetingsBonusValue;
    const trustBuilderSurveysReceived = parseFloat(formData.customerExperience.surveys.trustBuilderScore.surveysReceived || "0");
    const trustBuilderAvgScore = parseFloat(formData.customerExperience.surveys.trustBuilderScore.avgScore || "0");
    const closingComplete = parseFloat(formData.customerExperience.surveys.closingSurveyScore.complete || "0");
    const closingOnTime = parseFloat(formData.customerExperience.surveys.closingSurveyScore.onTime || "0");
    const closingCustomerRating = parseFloat(formData.customerExperience.surveys.closingSurveyScore.customerRatingScore || "0");
    const overallTrustBuilders = trustBuilderAvgScore;
    const overallComplete = closingComplete;
    const overallOnTime = closingOnTime;
    const overallCustomerRating = closingCustomerRating;
    const overallTotalAvgScore = trustBuilderSurveysReceived > 0 ? ((overallTrustBuilders + overallComplete + overallOnTime + overallCustomerRating) / 4) : 0;
    let surveysBonusPercentEarned = 0;
    if (overallTotalAvgScore >= 5.0) surveysBonusPercentEarned = 120;
    else if (overallTotalAvgScore >= 4.8) surveysBonusPercentEarned = 110;
    else if (overallTotalAvgScore >= 4.6) surveysBonusPercentEarned = 100;
    else if (overallTotalAvgScore >= 4.4) surveysBonusPercentEarned = 90;
    else if (overallTotalAvgScore >= 4.2) surveysBonusPercentEarned = 80;
    else if (overallTotalAvgScore >= 4.0) surveysBonusPercentEarned = 70;
    const surveysBonusValue = parseFloat(formData.customerExperience.surveys.bonusValue || "0.1");
    const surveysScore = surveysBonusPercentEarned * surveysBonusValue;
    const totalScore = Math.ceil(subdivisionScore + elmScore + expenseScore + callsScore + meetingsScore + surveysScore);
    const percentage = totalScore / 100;
    const revenueOfHomesClosed = parseFloat(formData.bonusCalculation.totalAchievementScore.revenueOfHomesClosed || "0");
    const overridePercentage = parseFloat(formData.bonusCalculation.totalAchievementScore.overridePercentage || "0.08");
    const bonusPoolAvailableAt100 = (revenueOfHomesClosed * (overridePercentage / 100));
    const totalMaxAt120 = bonusPoolAvailableAt100 * 1.2;
    const bonusPool = bonusPoolAvailableAt100;
    const totalBonusPayout = bonusPool * percentage;
    return {
      subdivisionPercentOfBenchmark: subdivisionPercentOfBenchmark.toFixed(2), subdivisionBonusPercentEarned: subdivisionBonusPercentEarned.toFixed(2), subdivisionScore: subdivisionScore.toFixed(2),
      elmGoal: elmGoal.toFixed(2), elmPercentOfRevenue: elmPercentOfRevenue.toFixed(2), elmBonusPercentEarned: elmBonusPercentEarned.toFixed(2), elmScore: elmScore.toFixed(2),
      expensePercentOfBudget: (expensePercentOfBudget * 100).toFixed(2), expenseBonusPercentEarned: expenseBonusPercentEarned.toFixed(2), expenseScore: expenseScore.toFixed(2),
      callsPercentUnder7: callsPercentUnder7.toFixed(2), callsPercentVsBenchmark: callsPercentVsBenchmark.toFixed(2), callsBonusPercentEarned: callsBonusPercentEarned.toFixed(2), callsScore: callsScore.toFixed(2),
      meetingsPercentCompleted: meetingsPercentCompleted.toFixed(2), meetingsPercentVsBenchmark: meetingsPercentVsBenchmark.toFixed(2), meetingsBonusPercentEarned: meetingsBonusPercentEarned.toFixed(2), meetingsScore: meetingsScore.toFixed(2),
      overallTrustBuilders: overallTrustBuilders.toFixed(2), overallComplete: overallComplete.toFixed(2), overallOnTime: overallOnTime.toFixed(2), overallCustomerRating: overallCustomerRating.toFixed(2),
      overallTotalAvgScore: overallTotalAvgScore.toFixed(2), surveysBonusPercentEarned: surveysBonusPercentEarned.toFixed(2), surveysScore: surveysScore.toFixed(2),
      bonusPoolAvailableAt100: bonusPoolAvailableAt100.toFixed(2), totalMaxAt120: totalMaxAt120.toFixed(2), totalScore: totalScore.toFixed(2), percentage: percentage.toFixed(2), bonusPool: bonusPool.toFixed(2), totalBonusPayout: totalBonusPayout.toFixed(2),
    };
  }, [
    formData.operationalEfficiency.subdivisionProduction.benchmark, formData.operationalEfficiency.subdivisionProduction.actualProduction, formData.operationalEfficiency.subdivisionProduction.bonusValue,
    formData.operationalEfficiency.extraLaborAndMaterial.benchmark, formData.operationalEfficiency.extraLaborAndMaterial.actualELM, formData.operationalEfficiency.extraLaborAndMaterial.bonusValue,
    formData.operationalEfficiency.customerCareExpense.actualExpense, formData.operationalEfficiency.customerCareExpense.budget, formData.operationalEfficiency.customerCareExpense.bonusValue,
    formData.customerExperience.customerCareCalls.under7Completed, formData.customerExperience.customerCareCalls.totalCallsCompleted, formData.customerExperience.customerCareCalls.bonusValue,
    formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days, formData.customerExperience.postCloseTouchpoint.totalMeetings, formData.customerExperience.postCloseTouchpoint.bonusValue,
    formData.customerExperience.surveys.trustBuilderScore.surveysReceived, formData.customerExperience.surveys.trustBuilderScore.avgScore,
    formData.customerExperience.surveys.closingSurveyScore.complete, formData.customerExperience.surveys.closingSurveyScore.onTime, formData.customerExperience.surveys.closingSurveyScore.customerRatingScore,
    formData.customerExperience.surveys.bonusValue, formData.bonusCalculation.totalAchievementScore.revenueOfHomesClosed, formData.bonusCalculation.totalAchievementScore.overridePercentage,
  ]);

  useEffect(() => {
    setFormData((prev) => {
      if (
        prev.operationalEfficiency.subdivisionProduction.percentOfBenchmark === derivedCalculations.subdivisionPercentOfBenchmark &&
        prev.operationalEfficiency.subdivisionProduction.bonusPercentEarned === derivedCalculations.subdivisionBonusPercentEarned &&
        prev.operationalEfficiency.subdivisionProduction.score === derivedCalculations.subdivisionScore &&
        prev.operationalEfficiency.extraLaborAndMaterial.goal === derivedCalculations.elmGoal &&
        prev.operationalEfficiency.extraLaborAndMaterial.percentOfRevenue === derivedCalculations.elmPercentOfRevenue &&
        prev.operationalEfficiency.extraLaborAndMaterial.bonusPercentEarned === derivedCalculations.elmBonusPercentEarned &&
        prev.operationalEfficiency.extraLaborAndMaterial.score === derivedCalculations.elmScore &&
        prev.operationalEfficiency.customerCareExpense.percentOfBudget === derivedCalculations.expensePercentOfBudget &&
        prev.operationalEfficiency.customerCareExpense.bonusPercentEarned === derivedCalculations.expenseBonusPercentEarned &&
        prev.operationalEfficiency.customerCareExpense.score === derivedCalculations.expenseScore &&
        prev.customerExperience.customerCareCalls.percentCompletedUnder7 === derivedCalculations.callsPercentUnder7 &&
        prev.customerExperience.customerCareCalls.percentVsBenchmark === derivedCalculations.callsPercentVsBenchmark &&
        prev.customerExperience.customerCareCalls.bonusPercentEarned === derivedCalculations.callsBonusPercentEarned &&
        prev.customerExperience.customerCareCalls.score === derivedCalculations.callsScore &&
        prev.customerExperience.postCloseTouchpoint.percentCompleted === derivedCalculations.meetingsPercentCompleted &&
        prev.customerExperience.postCloseTouchpoint.percentVsBenchmark === derivedCalculations.meetingsPercentVsBenchmark &&
        prev.customerExperience.postCloseTouchpoint.bonusPercentEarned === derivedCalculations.meetingsBonusPercentEarned &&
        prev.customerExperience.postCloseTouchpoint.score === derivedCalculations.meetingsScore &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.trustBuilders === derivedCalculations.overallTrustBuilders &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.complete === derivedCalculations.overallComplete &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.onTime === derivedCalculations.overallOnTime &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.customerRatingScore === derivedCalculations.overallCustomerRating &&
        prev.customerExperience.surveys.overallCombinedSurveyScore.totalAvgScore === derivedCalculations.overallTotalAvgScore &&
        prev.customerExperience.surveys.bonusPercentEarned === derivedCalculations.surveysBonusPercentEarned &&
        prev.customerExperience.surveys.score === derivedCalculations.surveysScore &&
        prev.bonusCalculation.totalAchievementScore.bonusPoolAvailableAt100 === derivedCalculations.bonusPoolAvailableAt100 &&
        prev.bonusCalculation.totalAchievementScore.totalMaxAt120 === derivedCalculations.totalMaxAt120 &&
        prev.bonusCalculation.totalScore === derivedCalculations.totalScore &&
        prev.bonusCalculation.percentage === derivedCalculations.percentage &&
        prev.bonusCalculation.bonusPool === derivedCalculations.bonusPool &&
        prev.bonusCalculation.totalBonusPayout === derivedCalculations.totalBonusPayout
      ) { return prev; }
      return {
        ...prev,
        operationalEfficiency: {
          ...prev.operationalEfficiency,
          subdivisionProduction: { ...prev.operationalEfficiency.subdivisionProduction, percentOfBenchmark: derivedCalculations.subdivisionPercentOfBenchmark, bonusPercentEarned: derivedCalculations.subdivisionBonusPercentEarned, score: derivedCalculations.subdivisionScore },
          extraLaborAndMaterial: { ...prev.operationalEfficiency.extraLaborAndMaterial, goal: derivedCalculations.elmGoal, percentOfRevenue: derivedCalculations.elmPercentOfRevenue, bonusPercentEarned: derivedCalculations.elmBonusPercentEarned, score: derivedCalculations.elmScore },
          customerCareExpense: { ...prev.operationalEfficiency.customerCareExpense, percentOfBudget: derivedCalculations.expensePercentOfBudget, bonusPercentEarned: derivedCalculations.expenseBonusPercentEarned, score: derivedCalculations.expenseScore },
        },
        customerExperience: {
          ...prev.customerExperience,
          customerCareCalls: { ...prev.customerExperience.customerCareCalls, percentCompletedUnder7: derivedCalculations.callsPercentUnder7, percentVsBenchmark: derivedCalculations.callsPercentVsBenchmark, bonusPercentEarned: derivedCalculations.callsBonusPercentEarned, score: derivedCalculations.callsScore },
          postCloseTouchpoint: { ...prev.customerExperience.postCloseTouchpoint, percentCompleted: derivedCalculations.meetingsPercentCompleted, percentVsBenchmark: derivedCalculations.meetingsPercentVsBenchmark, bonusPercentEarned: derivedCalculations.meetingsBonusPercentEarned, score: derivedCalculations.meetingsScore },
          surveys: { ...prev.customerExperience.surveys, overallCombinedSurveyScore: { ...prev.customerExperience.surveys.overallCombinedSurveyScore, trustBuilders: derivedCalculations.overallTrustBuilders, complete: derivedCalculations.overallComplete, onTime: derivedCalculations.overallOnTime, customerRatingScore: derivedCalculations.overallCustomerRating, totalAvgScore: derivedCalculations.overallTotalAvgScore }, bonusPercentEarned: derivedCalculations.surveysBonusPercentEarned, score: derivedCalculations.surveysScore },
        },
        bonusCalculation: { ...prev.bonusCalculation, totalAchievementScore: { ...prev.bonusCalculation.totalAchievementScore, bonusPoolAvailableAt100: derivedCalculations.bonusPoolAvailableAt100, totalMaxAt120: derivedCalculations.totalMaxAt120 }, totalScore: derivedCalculations.totalScore, percentage: derivedCalculations.percentage, bonusPool: derivedCalculations.bonusPool, totalBonusPayout: derivedCalculations.totalBonusPayout },
      };
    });
  }, [derivedCalculations]);

  useEffect(() => {
    return () => {
      if (under7DebounceRef.current) clearTimeout(under7DebounceRef.current);
      if (meetingsDebounceRef.current) clearTimeout(meetingsDebounceRef.current);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, options?: { type?: 'number' | 'decimal' | 'text', maxLength?: number, minLength?: number, max?: number, min?: number }) => {
    let { name, value } = e.target;
    if (options) {
      if (options.maxLength !== undefined && value.length > options.maxLength) return;
      if (options.minLength !== undefined && value !== '' && value.length < options.minLength) return;
    }
    value = typeof value === "string" ? value.replace(/,/g, "").trim() : "";
    const type = options?.type || "text";
    if (type === "number") { if (!(value === '' || /^[0-9]*$/.test(value))) return; }
    else if (type === "decimal") { if (!(value === '' || /^\d*\.?\d{0,2}$/.test(value))) return; }
    if (name === "customerExperience.customerCareCalls.under7Completed") {
      const totalCallsStr = formData.customerExperience.customerCareCalls.totalCallsCompleted;
      const totalCalls = parseInt(totalCallsStr || "0");
      const under7Value = parseInt(value || "0");
      if (totalCallsStr && totalCallsStr !== "" && totalCalls > 0 && under7Value > totalCalls) value = totalCalls.toString();
    }
    if (name === "customerExperience.customerCareCalls.totalCallsCompleted") {
      if (under7DebounceRef.current) clearTimeout(under7DebounceRef.current);
      const keys = name.split(".");
      setFormData((prev) => setByPath(prev, keys, value));
      const under7Str = formData.customerExperience.customerCareCalls.under7Completed;
      const under7 = parseInt(under7Str || "0");
      const totalCallsValue = parseInt(value || "0");
      if (under7Str && under7Str !== "" && under7 > 0 && value !== '' && totalCallsValue < under7) {
        under7DebounceRef.current = setTimeout(() => { setFormData((prev) => setByPath(prev, ["customerExperience", "customerCareCalls", "under7Completed"], value)); }, 500);
      }
      return;
    }
    if (name === "customerExperience.postCloseTouchpoint.meetingsCompleted30Days") {
      const totalMeetingsStr = formData.customerExperience.postCloseTouchpoint.totalMeetings;
      const totalMeetings = parseInt(totalMeetingsStr || "0");
      const meetingsValue = parseInt(value || "0");
      if (totalMeetingsStr && totalMeetingsStr !== "" && totalMeetings > 0 && meetingsValue > totalMeetings) value = totalMeetings.toString();
    }
    if (name === "customerExperience.postCloseTouchpoint.totalMeetings") {
      if (meetingsDebounceRef.current) clearTimeout(meetingsDebounceRef.current);
      const keys = name.split(".");
      setFormData((prev) => setByPath(prev, keys, value));
      const meetingsStr = formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days;
      const meetings = parseInt(meetingsStr || "0");
      const totalMeetingsValue = parseInt(value || "0");
      if (meetingsStr && meetingsStr !== "" && meetings > 0 && value !== '' && totalMeetingsValue < meetings) {
        meetingsDebounceRef.current = setTimeout(() => { setFormData((prev) => setByPath(prev, ["customerExperience", "postCloseTouchpoint", "meetingsCompleted30Days"], value)); }, 500);
      }
      return;
    }
    const keys = name.split(".");
    setFormData((prev) => setByPath(prev, keys, value));
  };

  const handleProjectSelect = (option: any) => {
    const project = projects.find(p => p.projectName === option?.value) || null;
    setSelectedProject(project);
    if (project) setFormData((prev) => ({ ...prev, communityNumber: project.projectNumber }));
  };

  const validateForm = () => {
    const errors: string[] = [];
    if (isReviewMode && (bonusDetails?.currentStatus === undefined || bonusDetails?.currentStatus === "Draft" || bonusDetails?.currentStatus === "Rejected")) {
      if (!formData.communityNumber) errors.push("Community is not selected.");
      if (!formData.operationalEfficiency.subdivisionProduction.benchmark || formData.operationalEfficiency.subdivisionProduction.benchmark === "0") errors.push("Subdivision Production: Benchmark is required.");
      if (!formData.operationalEfficiency.subdivisionProduction.actualProduction) errors.push("Subdivision Production: Actual Production is required.");
      if (!formData.operationalEfficiency.extraLaborAndMaterial.benchmark) errors.push("Extra Labor and Material: Benchmark is required.");
      if (!formData.operationalEfficiency.extraLaborAndMaterial.actualELM) errors.push("Extra Labor and Material: Actual ELM is required.");
      if (!formData.operationalEfficiency.customerCareExpense.actualExpense) errors.push("Customer Care Expense: Actual Expense is required.");
      if (!formData.operationalEfficiency.customerCareExpense.budget || formData.operationalEfficiency.customerCareExpense.budget === "0") errors.push("Customer Care Expense: Budget is required.");
      if (!formData.customerExperience.customerCareCalls.under7Completed) errors.push("Customer Care Calls: Under 7 Completed is required.");
      if (!formData.customerExperience.customerCareCalls.totalCallsCompleted) errors.push("Customer Care Calls: Total Calls Completed is required.");
      if (!formData.customerExperience.postCloseTouchpoint.meetingsCompleted30Days) errors.push("Post Close Touchpoint: Meetings Completed 30 Days is required.");
      if (!formData.customerExperience.postCloseTouchpoint.totalMeetings) errors.push("Post Close Touchpoint: Total Meetings is required.");
      if (!formData.customerExperience.surveys.trustBuilderScore.surveysReceived && formData.customerExperience.surveys.trustBuilderScore.surveysReceived !== "0") errors.push("Surveys: Trust Builder Score - Surveys Received is required.");
      if (!formData.customerExperience.surveys.trustBuilderScore.avgScore) errors.push("Surveys: Trust Builder Score - Average Score is required.");
      if (!formData.customerExperience.surveys.closingSurveyScore.complete) errors.push("Surveys: Closing Survey Score - Complete is required.");
      if (!formData.customerExperience.surveys.closingSurveyScore.onTime) errors.push("Surveys: Closing Survey Score - On Time is required.");
      if (!formData.customerExperience.surveys.closingSurveyScore.customerRatingScore) errors.push("Surveys: Closing Survey Score - Customer Rating Score is required.");
      if (!formData.bonusCalculation.totalAchievementScore.revenueOfHomesClosed && formData.bonusCalculation.totalAchievementScore.revenueOfHomesClosed !== "0") errors.push("Bonus Calculations: Revenue of Homes Closed is required.");
    }
    return errors;
  };

  const hasOverriddenHierarchy = initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy;
  const recipientEmail = initQueryData?.bonusRecipientDetails?.Email || bonusDetails?.bonusRecipientEmail;
  const recipientName = initQueryData?.bonusRecipientDetails?.EmployeeName || bonusDetails?.employeeName;
  const recipientJobTitle = initQueryData?.bonusRecipientDetails?.JobTitle || bonusSheetData?.jobTitle || formData.jobTitle || "";
  const recipientJobFamily = initQueryData?.bonusRecipientDetails?.JobFamily || bonusSheetData?.jobFamily || formData.jobFamily || "";
  const applicationType = initQueryData?.applicationType || bonusDetails?.applicationType;

  const buildMetadata = () => {
    const recipientNameFinal = recipientName || formData.bonusRecipientName || bonusDetails?.employeeName || "";
    const recipientEmailFinal = recipientEmail || formData.bonusRecipientEmail || "";
    return {
      applicationType: applicationType || formData.applicationType || "",
      bonusRecipientName: recipientNameFinal,
      bonusRecipientEmail: recipientEmailFinal,
      managerName: recipientNameFinal,
      managerEmail: recipientEmailFinal,
      applicantName: recipientNameFinal,
      applicantEmail: recipientEmailFinal,
      submitterName: initQueryData?.submitterName || bonusDetails?.submittedByName || formData.submitterName || "",
      submitterEmail: initQueryData?.submitterEmail || bonusDetails?.submittedBy || formData.submitterEmail || "",
      vpName: formData.vpName || "",
      vpEmail: formData.vpEmail || "",
      opsName: formData.opsName || "",
      opsEmail: formData.opsEmail || "",
      hasOverriddenHierarchy: formData.hasOverriddenHierarchy || false,
      jobTitle: recipientJobTitle || formData?.jobTitle || "",
      jobFamily: recipientJobFamily || formData?.jobFamily || "",
    };
  };

  const getBonusSheetJson = () => {
    const jsonString = JSON.stringify({
      communityNumber: formData.communityNumber, year: formData.year, quarter: formData.quarter,
      operationalEfficiency: formData.operationalEfficiency, customerExperience: formData.customerExperience,
      bonusCalculation: formData.bonusCalculation, signatures: formData.signatures,
      ...buildMetadata(),
    });
    return btoa(jsonString);
  };

  const breadcrumbPages = [
    {
      name: FORM_CONSTANTS.BREADCRUMB.TITLE,
      href: FORM_CONSTANTS.BREADCRUMB.PATH,
      current: true,
      icon: (props: React.ComponentProps<"svg">) => (
        <ClipboardDocumentCheckIcon {...props} className="w-6 h-6 text-primary-500 transition duration-75 group-hover:text-gray-900" />
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--background)" }}>
        <div style={{ color: "var(--muted-foreground)" }}>Loading…</div>
      </div>
    );
  }

  // ── Status badge color ────────────────────────────────────────────────────
  const status = bonusDetails?.currentStatus?.replaceAll("_", " ") ?? "Draft";
  const statusColors: Record<string, string> = {
    Draft: "background:#FEF9C3;color:#854D0E;border:1px solid #FDE047",
    Submitted: "background:#DBEAFE;color:#1E40AF;border:1px solid #93C5FD",
    Approved: "background:#DCFCE7;color:#166534;border:1px solid #86EFAC",
    Rejected: "background:#FEE2E2;color:#991B1B;border:1px solid #FCA5A5",
    Paid: "background:#D1FAE5;color:#065F46;border:1px solid #6EE7B7",
  };
  const statusStyle = statusColors[status] ?? statusColors["Draft"];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--background)" }}>
      <div className="flex flex-col">
        <main className="flex flex-col pb-8">
          <div className={`mx-auto w-full pt-6 ${FORM_CONSTANTS.LAYOUT.MAX_WIDTH} ${FORM_CONSTANTS.LAYOUT.PADDING}`}>
            <Breadcrumb pages={breadcrumbPages} className="mb-6 ml-2" />

            {/* ── Main card ──────────────────────────────────────────────── */}
            <div style={{ background: "var(--card)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>

              {/* Card header — clean, no heavy blue background */}
              <div style={{ borderBottom: "1px solid var(--border)", padding: "20px 24px", background: "var(--bg-form-header)" }}>
                {/* Title row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 700, color: "var(--card-foreground)", letterSpacing: "-0.01em" }}>
                    {bonusSheetTitle}
                  </h1>
                  {/* Status badge */}
                  <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 20, ...Object.fromEntries(statusStyle.split(";").map(s => { const [k, v] = s.split(":"); return [k.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase()), v?.trim()]; }).filter(([k]) => k)) }}>
                    {status}
                  </span>
                </div>
                {/* Disclaimer */}
                <p style={{ margin: "0 0 16px", fontSize: 12, color: "var(--muted-foreground)", fontStyle: "italic", lineHeight: 1.5 }}>
                  Achievement of Targets is a subjective determination in the Employer's sole discretion. The Employer may change the percentages or Targets at any time.
                </p>

                {/* Form fields row */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                  {/* Bonus Recipient */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--muted-foreground)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Bonus Recipient</label>
                    <input type="text" name="managerName" value={formData.managerName} readOnly
                      style={{ width: "100%", padding: "7px 10px", fontSize: 13, fontWeight: 500, color: "var(--card-foreground)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 7, outline: "none", cursor: "not-allowed", boxSizing: "border-box" }}
                    />
                  </div>
                  {/* Year */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--muted-foreground)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Year</label>
                    <input type="text" name="year" value={formData.year} readOnly
                      style={{ width: "100%", padding: "7px 10px", fontSize: 13, color: "var(--muted-foreground)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 7, outline: "none", cursor: "not-allowed", boxSizing: "border-box" }}
                    />
                  </div>
                  {/* Quarter */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--muted-foreground)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Quarter</label>
                    <input type="text" name="quarter" value={formData.quarter} readOnly
                      style={{ width: "100%", padding: "7px 10px", fontSize: 13, color: "var(--muted-foreground)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 7, outline: "none", cursor: "not-allowed", boxSizing: "border-box" }}
                    />
                  </div>
                  {/* Community # */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--muted-foreground)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Community #</label>
                    <Select
                      name="communityNumber"
                      instanceId="communityNumber"
                      isDisabled={Boolean(bonusDetails?.bonusApplicationId)}
                      components={{
                        Group: CustomCommunityGroup,
                        GroupHeading: CustomCommunityGroupHeading
                      }}
                      options={groupedOptions}
                      filterOption={filterOption}
                      placeholder="Search by region, name, or number..."
                      value={
                        selectedProject
                          ? { label: `${selectedProject.projectNumber} - ${selectedProject.projectName}`, value: selectedProject.projectName }
                          : groupedOptions.flatMap(g => g.options).find(o => o.value === formData.communityNumber) || null
                      }
                      onChange={(option) => {
                        // Mimic the original handleProjectSelect event structure or pass option directly
                        handleProjectSelect(option);
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          width: "100%",
                          fontSize: 13,
                          minHeight: 33,
                          borderRadius: 7,
                          border: "1px solid var(--input)",
                          background: "var(--background)",
                          cursor: Boolean(bonusDetails?.bonusApplicationId) ? "not-allowed" : "pointer",
                          opacity: Boolean(bonusDetails?.bonusApplicationId) ? 0.7 : 1,
                        }),
                        option: (base) => ({ ...base, fontSize: 13 }),
                        groupHeading: (base) => ({ ...base, fontWeight: 'bold', color: 'var(--foreground)' })
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Override active banner */}
              {hasOverriddenHierarchy && (
                <div style={{ background: "#fff5f5", border: "1.5px solid #f87171", borderRadius: "8px", padding: "12px 16px", fontSize: 12, color: "#991b1b", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 14 }}>⚠️</span>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>Override Active: Employee Hierarchy is Overridden</span>
                  </div>
                  <span style={{ color: "#7f1d1d", lineHeight: 1.5 }}>
                    {status === "Draft"
                      ? "This recipient was selected with an active hierarchy override — all Construction & Customer Care employees were available for selection, not just direct reports."
                      : "This recipient was selected under a hierarchy override — the standard reporting hierarchy was not enforced at the time of selection."}
                  </span>
                  {(recipientName || recipientJobTitle || recipientJobFamily || applicationType) && (
                    <div style={{ marginTop: 4, padding: "8px 10px", background: "#fee2e2", borderRadius: "6px", display: "flex", flexWrap: "wrap", gap: "4px 20px", color: "#7f1d1d" }}>
                      {recipientName && <span><span style={{ fontWeight: 600 }}>Recipient:</span> {recipientName}</span>}
                      {recipientJobTitle && <span><span style={{ fontWeight: 600 }}>Recipient Job Title:</span> {recipientJobTitle}</span>}
                      {recipientJobFamily && <span><span style={{ fontWeight: 600 }}>Job Family:</span> {recipientJobFamily}</span>}
                      {applicationType && <span><span style={{ fontWeight: 600 }}>Application Type:</span> {applicationType}</span>}
                    </div>
                  )}
                </div>
              )}

              {/* ── OPERATIONAL EFFICIENCY ─────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border)" }}>
                {/* Section header — left accent bar instead of full-width orange bg */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--primary)", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--muted-foreground)" }}>
                    Operational Efficiency
                  </span>
                </div>

                <SubdivisionProductionSubForm
                  data={formData.operationalEfficiency.subdivisionProduction}
                  isEditable={isEditable}
                  percentOfTotalAchievementScore={35}
                  onInputChange={handleInputChange}
                />
                <ExtraLaborAndMaterialSubForm
                  data={formData.operationalEfficiency.extraLaborAndMaterial}
                  isEditable={isEditable}
                  percentOfTotalAchievementScore={15}
                  onInputChange={handleInputChange}
                />
                <CustomerCareExpenseSubForm
                  data={formData.operationalEfficiency.customerCareExpense}
                  isEditable={isEditable}
                  onInputChange={handleInputChange}
                />
              </div>

              {/* ── CUSTOMER EXPERIENCE ────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 24px", background: "var(--bg-section-header)", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: "var(--primary)", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: "var(--muted-foreground)" }}>
                    Customer Experience
                  </span>
                </div>

                <CustomerCareCallsSubForm
                  data={formData.customerExperience.customerCareCalls}
                  isEditable={isEditable}
                  percentOfTotalAchievementScore={20}
                  onInputChange={handleInputChange}
                />
                <PostCloseTouchpointSubForm
                  data={formData.customerExperience.postCloseTouchpoint}
                  isEditable={isEditable}
                  onInputChange={handleInputChange}
                />
                <SurveysSubForm
                  data={formData.customerExperience.surveys}
                  isEditable={isEditable}
                  onInputChange={handleInputChange}
                />
              </div>

              {/* ── BONUS CALCULATION ──────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border)" }}>
                <BonusCalculationCM3PMSubForm
                  data={formData.bonusCalculation}
                  isEditable={isEditable}
                  onInputChange={handleInputChange}
                />
              </div>
            </div>
          </div>
        </main>
      </div>

      <BonusApplicationHistory
        historyItems={historyItems}
        comments={commentList}
        historyAccordionExpanded={historyAccordionExpanded}
        onAccordionToggle={handleHistoryAccordionToggle}
      />

      <CommentComponent
        comment={comment}
        commentList={commentList}
        commentsAccordionExpanded={commentsAccordionExpanded}
        onCommentChange={handleCommentChange}
        onAddComment={handleAddComment}
        onAccordionToggle={handleCommentsAccordionToggle}
      />

      {/* ── Footer action bar ─────────────────────────────────────────────── */}
      <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border)", background: "var(--muted)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <ActionButtons
          isReviewMode={isReviewMode}
          bonusApplicationId={id && parseInt(id) ? parseInt(id) : bonusDetails?.bonusApplicationId}
          currentStatus={bonusDetails?.currentStatus ?? null}
          bonusSheetJson={getBonusSheetJson()}
          projectNumbers={formData.communityNumber}
          amount={parseFloat(formData.bonusCalculation.totalBonusPayout) || 0}
          bonusRecipientEmail={formData.bonusRecipientEmail}
          submitterEmail={formData.submitterEmail}
          submitterName={formData.submitterName}
          vpEmail={formData.vpEmail}
          applicationType={formData.applicationType}
          hasOverriddenHierarchy={initQueryData?.hasOverriddenHierarchy || bonusDetails?.hasOverriddenHierarchy || false}
          comments={commentList.filter((comment: BonusApplicationComment) => comment.commentId <= 0).map((comment: BonusApplicationComment) => comment.commentText)}
          formValidationErrors={validateForm()}
          recipientName={formData.managerName}
          recipientJobFamily={recipientJobFamily}
          recipientJobTitle={recipientJobTitle}
          loggedInUserName={loggedInUser.name}

        />
      </div>
    </div>
  );
};

export default CM3PMForm;